// 191 -- same-field schedule overlap detection.
//
// One rule, used by BOTH the portal scheduler (red framing while editing) and
// the API publish check (warn-and-confirm gate), so the two cannot drift:
// two matches conflict when they belong to the same non-null event_court_id
// and their [starts_at, ends_at) ranges intersect. Half-open semantics --
// back-to-back matches (one ends exactly when the next starts) do NOT
// conflict. Matches with no court assigned never conflict.

export interface ScheduleConflictMatch {
  id: string;
  event_court_id: string | null;
  starts_at: string | Date;
  ends_at: string | Date;
}

/** One overlapping pair on a field. `a` always starts at or before `b`. */
export interface ScheduleConflictPair {
  event_court_id: string;
  a_id: string;
  b_id: string;
}

function toMs(v: string | Date): number {
  return typeof v === "string" ? new Date(v).getTime() : v.getTime();
}

export function findScheduleConflicts(
  matches: ScheduleConflictMatch[],
): ScheduleConflictPair[] {
  const byCourt = new Map<string, ScheduleConflictMatch[]>();
  for (const m of matches) {
    if (!m.event_court_id) continue;
    const list = byCourt.get(m.event_court_id) ?? [];
    list.push(m);
    byCourt.set(m.event_court_id, list);
  }

  const pairs: ScheduleConflictPair[] = [];
  for (const [courtId, list] of byCourt) {
    const sorted = [...list].sort((x, y) => toMs(x.starts_at) - toMs(y.starts_at));
    for (let i = 0; i < sorted.length; i++) {
      const a = sorted[i];
      const aEnd = toMs(a.ends_at);
      // Sorted by start, so only later matches can begin inside `a`; stop at
      // the first one that starts at/after a's end (half-open: touching is ok).
      for (let j = i + 1; j < sorted.length; j++) {
        const b = sorted[j];
        if (toMs(b.starts_at) >= aEnd) break;
        pairs.push({ event_court_id: courtId, a_id: a.id, b_id: b.id });
      }
    }
  }
  return pairs;
}

/** Convenience for the scheduler UI: the set of match ids in any conflict. */
export function conflictingMatchIds(
  matches: ScheduleConflictMatch[],
): Set<string> {
  const ids = new Set<string>();
  for (const p of findScheduleConflicts(matches)) {
    ids.add(p.a_id);
    ids.add(p.b_id);
  }
  return ids;
}

/** 191 -- one entry of the publish 409 `conflicts` payload (self-sufficient:
 *  carries names + times so any client can render the warning without a
 *  second fetch). */
export interface PublishScheduleConflict {
  event_court_id: string;
  court_name: string | null;
  a: { id: string; name: string; starts_at: string; ends_at: string };
  b: { id: string; name: string; starts_at: string; ends_at: string };
}

/** Discriminator for the publish 409 body (`code` field). */
export const SCHEDULE_CONFLICTS_CODE = "schedule_conflicts";
